import {
  AfterViewInit,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormModelControl } from '@bcl/common-ui';
import { SelectOption, TabsGroupComponent } from '@bcl/design-system';
import { Subject, takeUntil } from 'rxjs';
import {
  AddMapActionDialogData,
  AddMapActionDialogForm,
  AddMapActionDialogResponse,
} from './add-map-action-dialog.model';
import { ActionPointDictionariesService } from 'src/app/pages/action/action-dictionaries.service';
import { ActionType } from 'src/app/pages/action/action.model';
import { ObjectPlacerCoordinatesSelectorData } from '../../../../components';

@Component({
  selector: 'app-add-map-action-dialog',
  templateUrl: './add-map-action-dialog.component.html',
  styleUrls: ['./add-map-action-dialog.component.scss'],
})
export class AddMapActionDialogComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  public readonly form: FormGroup<FormModelControl<AddMapActionDialogForm>>;
  public actionTypeOptions: SelectOption[] = [];
  public readonly mapTabIndex = 0;
  public readonly formTabIndex = 1;
  public coordinatesSelectorData:
    | ObjectPlacerCoordinatesSelectorData
    | undefined;

  @ViewChild('tabsGroup')
  private tabsGroup!: TabsGroupComponent;

  private actionTypes: ActionType[] = [];

  private newActionCoordinates: { x: number; y: number } | undefined;

  private readonly destroyed$ = new Subject<void>();

  constructor(
    fb: FormBuilder,
    private dialogRef: MatDialogRef<
      AddMapActionDialogComponent,
      AddMapActionDialogResponse
    >,
    @Inject(MAT_DIALOG_DATA) private data: AddMapActionDialogData,
    private actionDictionariesService: ActionPointDictionariesService
  ) {
    dialogRef.updateSize('600px');
    dialogRef.disableClose = true;

    this.form = fb.group({
      actionTypeID: [null, Validators.required],
      title: [null, Validators.required],
      description: [null],
    }) as FormGroup<FormModelControl<AddMapActionDialogForm>>;
  }

  public ngOnInit(): void {
    this.loadActionTypes();
    this.setupActionTypeSubscription();
  }

  public ngAfterViewInit(): void {
    this.dialogRef.afterOpened().subscribe(() => {
      setTimeout(() => (this.coordinatesSelectorData = this.data.areaMapData));
    });
  }

  public onNewActionCoordinatesSelected(coordinates: {
    x: number;
    y: number;
  }): void {
    this.newActionCoordinates = coordinates;
    this.tabsGroup.selectTab(this.formTabIndex);
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public submit(): void {
    const formData = this.form.getRawValue();
    const result = {
      ...formData,
      mapCoordinates: this.newActionCoordinates,
      areaId: this.data.areaId,
    } as AddMapActionDialogResponse;

    this.dialogRef.close(result);
  }

  private loadActionTypes(): void {
    this.actionDictionariesService.getActionTypes().subscribe({
      next: (actionTypes) => {
        this.actionTypes = actionTypes;
        this.actionTypeOptions = actionTypes.map((type) => ({
          value: type.value,
          label: type.defaultTitle,
        }));
      },
      error: () => {
        // Handle error silently or show a message if needed
      },
    });
  }

  private setupActionTypeSubscription(): void {
    this.form.controls.actionTypeID.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((actionTypeId) => {
        const selectedActionType = this.actionTypes.find(
          (type) => type.value === actionTypeId
        );
        if (selectedActionType) {
          this.form.controls.title.setValue(selectedActionType.defaultTitle);
        }
      });
  }
}
